/**
 * Storage utility with sessionStorage + localStorage dual storage
 *
 * Telegram WebApp localStorage can be unreliable.
 * We use sessionStorage (survives page refresh) + localStorage (backup).
 */

import WebApp from '@twa-dev/sdk';
import { logger } from './logger';

// Disable Cloud Storage - not available in dev mode or browser
const USE_CLOUD_STORAGE = false;

// Use dual storage: sessionStorage (primary) + localStorage (backup)
const USE_DUAL_STORAGE = true;

/**
 * Save data to storage (Telegram Cloud or localStorage)
 */
export const storageSet = async (key: string, value: string): Promise<void> => {
  if (USE_CLOUD_STORAGE && WebApp.CloudStorage) {
    return new Promise((resolve) => {
      WebApp.CloudStorage.setItem(key, value, (error) => {
        if (error) {
          logger.error('Cloud storage set failed', { key, error });
          // Fallback to localStorage
          localStorage.setItem(key, value);
          resolve();
        } else {
          logger.debug('Cloud storage set success', { key });
          // Also save to localStorage as backup
          localStorage.setItem(key, value);
          resolve();
        }
      });
    });
  } else {
    // Use dual storage: sessionStorage + localStorage
    if (USE_DUAL_STORAGE) {
      try {
        sessionStorage.setItem(key, value);
        localStorage.setItem(key, value);

        // Verify immediately
        const sessionVerify = sessionStorage.getItem(key);
        const localVerify = localStorage.getItem(key);

        logger.debug('Dual storage set + verify', {
          key,
          valueLength: value.length,
          sessionSaved: sessionVerify === value,
          localSaved: localVerify === value,
          sessionLength: sessionVerify?.length,
          localLength: localVerify?.length,
        });

        if (!sessionVerify && !localVerify) {
          logger.error('❌ Storage blocked! Both session and local returned null after set', { key });
        }
      } catch (error) {
        logger.error('❌ Storage.setItem() exception', { key, error: String(error) });
      }
    } else {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        logger.error('❌ localStorage.setItem() exception', { key, error: String(error) });
      }
    }
    return Promise.resolve();
  }
};

/**
 * Get data from storage (Telegram Cloud or localStorage)
 */
export const storageGet = async (key: string): Promise<string | null> => {
  if (USE_CLOUD_STORAGE && WebApp.CloudStorage) {
    return new Promise((resolve) => {
      WebApp.CloudStorage.getItem(key, (error, value) => {
        if (error) {
          logger.error('Cloud storage get failed', { key, error });
          // Fallback to localStorage
          const localValue = localStorage.getItem(key);
          resolve(localValue);
        } else if (value) {
          logger.debug('Cloud storage get success', { key, valueLength: value.length });
          // Sync to localStorage as backup
          localStorage.setItem(key, value);
          resolve(value);
        } else {
          logger.debug('Cloud storage get returned null', { key });
          // Try localStorage fallback
          const localValue = localStorage.getItem(key);
          if (localValue) {
            logger.debug('Using localStorage fallback', { key });
          }
          resolve(localValue);
        }
      });
    });
  } else {
    // Use dual storage: sessionStorage first, then localStorage fallback
    if (USE_DUAL_STORAGE) {
      const sessionValue = sessionStorage.getItem(key);
      const localValue = localStorage.getItem(key);

      if (sessionValue) {
        logger.debug('Dual storage get: using sessionStorage', { key });
        return Promise.resolve(sessionValue);
      } else if (localValue) {
        logger.debug('Dual storage get: using localStorage fallback', { key });
        // Restore to sessionStorage
        sessionStorage.setItem(key, localValue);
        return Promise.resolve(localValue);
      } else {
        logger.debug('Dual storage get: not found', { key });
        return Promise.resolve(null);
      }
    } else {
      return Promise.resolve(localStorage.getItem(key));
    }
  }
};

/**
 * Remove data from storage (Telegram Cloud and localStorage)
 */
export const storageRemove = async (key: string): Promise<void> => {
  if (USE_CLOUD_STORAGE && WebApp.CloudStorage) {
    return new Promise((resolve) => {
      WebApp.CloudStorage.removeItem(key, (error) => {
        if (error) {
          logger.error('Cloud storage remove failed', { key, error });
        } else {
          logger.debug('Cloud storage remove success', { key });
        }
        // Also remove from localStorage
        localStorage.removeItem(key);
        resolve();
      });
    });
  } else {
    // Remove from both storages
    if (USE_DUAL_STORAGE) {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
      logger.debug('Dual storage remove', { key });
    } else {
      localStorage.removeItem(key);
    }
    return Promise.resolve();
  }
};

/**
 * Get multiple keys from storage
 */
export const storageGetMultiple = async (keys: string[]): Promise<Record<string, string | null>> => {
  if (USE_CLOUD_STORAGE && WebApp.CloudStorage) {
    return new Promise((resolve) => {
      WebApp.CloudStorage.getItems(keys, (error, values) => {
        if (error) {
          logger.error('Cloud storage getItems failed', { keys, error });
          // Fallback to localStorage
          const result: Record<string, string | null> = {};
          keys.forEach((key) => {
            result[key] = localStorage.getItem(key);
          });
          resolve(result);
        } else {
          logger.debug('Cloud storage getItems success', { keys, values });
          // Sync to localStorage
          Object.entries(values || {}).forEach(([key, value]) => {
            if (value) localStorage.setItem(key, value);
          });
          resolve(values || {});
        }
      });
    });
  } else {
    // Use localStorage
    const result: Record<string, string | null> = {};
    keys.forEach((key) => {
      result[key] = localStorage.getItem(key);
    });
    return Promise.resolve(result);
  }
};
